import { Router } from '@common/routes/Router';
import { useRedline } from '@libs/redline';
import { getAnonymousID, getSessionID } from 'redline-client-sdk';
import { addCookie, getCookie, getCookiesStartsWith } from '@libs/utils/helpers/getSafeCookies';
import constants from '@common/constants';
import { isProd } from '@libs/utils/helpers/isProd';
import { useEffect, useState } from 'react';

import { useUser } from '@common/hooks/useUser';
import useHotjar from '@libs/utils/hooks/useHotjar';

import { untilLoad, ClarityHelpers } from '@consumidor-positivo/ts-utils';
import { addFeatureFlagEvaluation, isDatadogInitialized } from '@libs/utils/helpers/datadogRum';
import { CustomExperienceService } from '@libs/renegkit';

import { useAuthentication } from '@libs/acp/contexts';
import PageLoader from '@common/components/PageLoader';
import { useWebBridgeContext } from '@libs/app-bridge';
import useGTM from '@common/hooks/useGTM';

import '@libs/atlas/styles/GlobalStyles.scss';

const appVersion: string = import.meta.env?.VITE_DEPLOY_VERSION || '';

const identifyClarity = async () => {
  await untilLoad(() => window?.cpRedline?.sessionID, 10000);
  ClarityHelpers.identify({
    userId: getAnonymousID() || 'not-loaded',
    sessionId: getSessionID() || 'not-loaded',
    pageId: window.location.pathname,
  });

  ClarityHelpers.setAppVersion(appVersion as `v${number}.${number}.${number}`);
};

function App() {
  const { isLoading } = useAuthentication();

  const searchParams = new URLSearchParams(window.location.search);
  const engageSigninData = searchParams.get('e');
  const shouldRemoveBrokeEngageParam = engageSigninData && engageSigninData?.startsWith('{{');

  const { track } = useRedline();
  const { appSessionTracking } = useWebBridgeContext();
  const { trigger } = useGTM();

  const anonymousId = getAnonymousID();
  const { customerIdHash } = useUser();
  const hotjar = useHotjar();
  const [sentForcedSessionStarted, setSendForcedSessionStarted] = useState(false);

  const rlTracksAuth = getCookie(constants.redline.rl_tracks_auth, true);
  const rlTracksAuthData = rlTracksAuth ? JSON.parse(rlTracksAuth) : null;
  const isAtendeFlow = window !== window.parent && (rlTracksAuthData?.authenticationType === 'atende-sign-in' || !!getCookie('ATENDE_SIGNUP'));

  function sendEventExperimentsDogx() {
    const dxCookies = getCookiesStartsWith('dx-');
    const { 'dx-bg': dxBG, ...dxCookiesOnly } = dxCookies;
    const hasTest = Object.keys(dxCookiesOnly).length;

    if (dxBG && isDatadogInitialized()) {
      addFeatureFlagEvaluation('dx-bg', appVersion, {
        version: appVersion,
        percentage: dxBG,
      });
    }

    if (hasTest) {
      const experiments: string[] = [];
      const experimentsResults: string[] = [];

      const dxExperiments = Object.keys(dxCookiesOnly)
        .map((name) => {
          const dxValues = dxCookiesOnly[name]?.split(':');
          if (!dxValues) return null;

          const [variant, version, percentage] = dxValues;

          experiments.push(name);
          experimentsResults.push(variant);

          if (isDatadogInitialized()) {
            addFeatureFlagEvaluation(name, variant, {
              version: version?.toString(),
              percentage: percentage?.toString(),
            });
          }

          return {
            name,
            variant,
            version: Number(version),
            percentage: Number(percentage),
          };
        })
        .filter((experiment) => experiment !== null);

      track.dogx.experimentsLoaded({
        experiments: dxExperiments,
      });

      // #region AB Test Sofia
      const sofiaVariant = dxCookiesOnly['dx-should-appear-sofia']?.split(':')[0] ?? 'control';
      trigger.sofiaExperimentReady({ sofiaVariant });
      // #endregion

      hotjar.add({
        ...(!!customerIdHash && { customerIdHash: customerIdHash }),
        anonymousId: anonymousId,

        sessionExperiments: experiments.join('|').slice(0, 199),
        sessionVariants: experimentsResults.join('|').slice(0, 199),
        appVersion,
      });
    } else {
      hotjar.add({
        ...(!!customerIdHash && { customerIdHash: customerIdHash }),
        anonymousId: anonymousId,
        appVersion,
      });
    }
  }

  useEffect(() => {
    forceSessionStarted();
    CustomExperienceService.startLeadsCustomExperience();
  }, []);

  async function forceSessionStarted() {
    await untilLoad(() => window?.cpRedline?.ready, 7000);
    if (!sentForcedSessionStarted) {
      window?.cpRedline?.triggerSessionStarted();
      setSendForcedSessionStarted(true);
    }
  }

  useEffect(() => {
    const url = new URL(window.location.href);

    if (shouldRemoveBrokeEngageParam) {
      url.searchParams.delete('e');
      window.location.replace(url.toString());
    }
  }, [shouldRemoveBrokeEngageParam]);

  useEffect(() => {
    if (isAtendeFlow) {
      const bodyHtml = document.querySelector('body');
      bodyHtml?.classList.add('atende-flow');
      bodyHtml?.style?.setProperty('overflow', 'auto');
    }
  }, [isAtendeFlow]);

  useEffect(() => {
    const startSourceUrl = getCookie('start_source_url');
    if (!startSourceUrl) {
      addCookie({
        key: 'start_source_url',
        value: window.location.href,
      });
    }

    sendEventExperimentsDogx();
  }, []);

  useEffect(() => {
    identifyClarity();
  }, []);

  useEffect(() => {
    hotjar.updateLoggedUser(customerIdHash);
  }, [customerIdHash]);

  useEffect(() => {
    if (isProd()) {
      window.__APPVERSION__ = appVersion;

      addCookie({
        key: 'appVersion',
        value: appVersion,
      });
    }
  }, []);

  useEffect(() => {
    appSessionTracking.fireTracking();
  }, []);

  if (isLoading) return <PageLoader />;

  return <Router />;
}

export default App;
